import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface VisitSlotMetadata {
  type: 'visit_slot_request' | 'visit_slot_proposal' | 'visit_slot_confirmed';
  contactRequestId: string;
  slotId?: string;
  visitDate?: string;
  visitDuration?: number;
  visitNotes?: string;
  clientName?: string;
  propertyTitle?: string;
  propertyAddress?: string;
  proposedBy?: 'admin' | 'client' | 'professional';
}

interface VisitSlotCardProps {
  content: string;
  metadata: VisitSlotMetadata;
  timestamp: string;
  isOwn: boolean;
  senderName?: string;
  role?: 'admin' | 'professional' | 'client';
}

export function VisitSlotCard({
  content,
  metadata,
  timestamp,
  isOwn,
  senderName,
  role,
}: VisitSlotCardProps) {
  const navigate = useNavigate();

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const handleViewDetails = () => {
    if (role === 'admin') {
      navigate(`/admin/contact-requests/${metadata.contactRequestId}`);
    } else if (role === 'professional') {
      navigate(`/professional/leads/${metadata.contactRequestId}`);
    } else {
      navigate(`/client/requests/${metadata.contactRequestId}`);
    }
  };

  const label =
    metadata.type === 'visit_slot_request'
      ? 'Créneau à planifier'
      : metadata.type === 'visit_slot_confirmed'
      ? 'Visite confirmée'
      : 'Proposition de créneau';

  return (
    <div
      className={cn(
        'flex w-full mb-4',
        isOwn ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl shadow-sm overflow-hidden',
          isOwn
            ? 'bg-[#FF9F68] text-white rounded-br-md'
            : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
        )}
      >
        {!isOwn && senderName && (
          <div className="px-4 pt-3">
            <p className="text-xs font-medium text-[#FF9F68]">
              {senderName}
            </p>
          </div>
        )}

        <div className="px-4 pt-3">
          <p
            className={cn(
              'text-sm whitespace-pre-wrap leading-relaxed',
              isOwn ? 'text-white' : 'text-gray-700'
            )}
          >
            {content}
          </p>
        </div>

        <div
          className={cn(
            'mx-3 my-3 p-4 rounded-xl cursor-pointer transition-all hover:shadow-md',
            isOwn
              ? 'bg-white/10 hover:bg-white/20 border border-white/20'
              : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
          )}
          onClick={handleViewDetails}
        >
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'p-2 rounded-lg',
                isOwn ? 'bg-white/20' : 'bg-[#FF9F68]/10'
              )}
            >
              <Calendar
                className={cn(
                  'h-5 w-5',
                  isOwn ? 'text-white' : 'text-[#FF9F68]'
                )}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4
                className={cn(
                  'font-semibold text-sm truncate',
                  isOwn ? 'text-white' : 'text-gray-900'
                )}
              >
                {label} {metadata.clientName ? `· ${metadata.clientName}` : ''}
              </h4>
              {metadata.visitDate && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Clock
                    className={cn(
                      'h-3.5 w-3.5 flex-shrink-0',
                      isOwn ? 'text-blue-200' : 'text-gray-500'
                    )}
                  />
                  <span
                    className={cn(
                      'text-xs truncate',
                      isOwn ? 'text-blue-100' : 'text-gray-600'
                    )}
                  >
                    {formatDate(metadata.visitDate)} à {formatTime(metadata.visitDate)}
                  </span>
                </div>
              )}
              {metadata.propertyTitle && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <MapPin
                    className={cn(
                      'h-3.5 w-3.5 flex-shrink-0',
                      isOwn ? 'text-blue-200' : 'text-gray-500'
                    )}
                  />
                  <span
                    className={cn(
                      'text-xs truncate',
                      isOwn ? 'text-blue-100' : 'text-gray-600'
                    )}
                  >
                    {metadata.propertyTitle}
                  </span>
                </div>
              )}
              {metadata.visitNotes && (
                <div className="mt-1 text-xs text-gray-500">
                  Notes : {metadata.visitNotes}
                </div>
              )}
            </div>
            <ExternalLink
              className={cn(
                'h-4 w-4 flex-shrink-0',
                isOwn ? 'text-blue-200' : 'text-gray-400'
              )}
            />
          </div>
        </div>

        <div
          className={cn(
            'px-4 pb-2 flex items-center',
            isOwn ? 'justify-end' : 'justify-start'
          )}
        >
          <span
            className={cn(
              'text-[10px]',
              isOwn ? 'text-blue-100' : 'text-gray-400'
            )}
          >
            {formatTime(timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default VisitSlotCard;

